import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { FaqCategory, FaqQna } from "@/model";
import { toSpliced, withArray } from "@/util/arrayUtil";
import { useSyncForm } from "@/hook/useSyncForm";
import { Textarea, TextInput } from "@/component/input";
import Button from "@/component/Button";
import { SortableItemWrapper, SortableWrapper, SortableItem, getUpdatedSortableList } from "@/component/SortableWrapper";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";


interface FAQInputGroupProps {
    faq: FaqCategory[];
    onChange: (faqCategories: FaqCategory[]) => void;
}

const FAQInputGroup: React.FC<FAQInputGroupProps> = (props) => {
    const { faq: categories, onChange } = props;

    const intl = useIntl();
    
    const onChangeName = (index: number, name: string) => {
        const prevCategory = categories[index];
        onChange(withArray(categories, index, { ...prevCategory, category: name }));
    };

    const onChangeQnaList = (index: number, qnaList: FaqQna[]) => {
        const prevCategory = categories[index];
        onChange(withArray(categories, index, { ...prevCategory, qna_list: qnaList }));
    };

    const onClickAddQna = (index: number) => {
        const prevCategory = categories[index];
        onChange(withArray(categories, index, { ...prevCategory, qna_list: [ ...prevCategory.qna_list, { question: '', answer: '' }] }));
    };

    return (
        <fieldset className="flex flex-col">
            <legend className="sr-only">
                {intl.formatMessage({ id: "i10031" })}
            </legend>
            <div className="h-input-default flex items-center gap-0.5">
                <div className="field-label">
                    {intl.formatMessage({ id: "i10031" })}
                </div>
                <Button variant="text"
                        onClick={() => onChange([...categories, { category: '', qna_list: [] }])}
                >
                    <AddIcon className="size-3.5" />
                    {intl.formatMessage({ id: "b0009" })}
                </Button>
            </div>

            <div className="flex flex-col gap-4">
                {categories.map((category, index) => (
                    <div key={index}
                         className="flex flex-col gap-2.5"
                    >
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-4">
                                <TextInput required
                                           aria-label={`${intl.formatMessage({ id: "i10031" })} ${index + 1}`}
                                           value={category.category}
                                           placeholder={intl.formatMessage({ id: "i10031" })}
                                           onChange={e => onChangeName(index, e.target.value)}
                                />
                                <button className="text-red-500 shrink-0"
                                        aria-label={intl.formatMessage({ id: "b0006" })}
                                        onClick={() => onChange(toSpliced(categories, index, 1))}
                                >
                                    <DeleteIcon className="size-4" />
                                </button>
                            </div>

                            <SortableQnaList qnaList={category.qna_list}
                                             onChange={qnaList => onChangeQnaList(index, qnaList)}
                            />
                        </div>
                            
                        <button className="p-1 self-center text-black-400"
                                aria-label={intl.formatMessage({ id: "b0009" })}
                                onClick={() => onClickAddQna(index)}
                        >
                            <AddIcon className="size-4" />
                        </button>
                    </div>
                ))}
            </div>
        </fieldset>
    );
};

interface SortableQnaListProps {
    qnaList: FaqQna[];
    onChange: (qnaList: FaqQna[]) => void;
}

const SortableQnaList: React.FC<SortableQnaListProps> = (props) => {
    const { qnaList, onChange: propsOnChange } = props;

    const intl = useIntl();

    const [sortableQnaList, setSortableQnaList] = useState<SortableItem<FaqQna>[]>([]);
    
    const update = (qnaList: SortableItem<FaqQna>[]) => {
        setSortableQnaList(qnaList);
        propsOnChange(qnaList.map(qna => qna.data));
    };

    const onChangeQnaQuestion = (index: number, question: string) => {
        const prevQna = sortableQnaList[index];
        update(withArray(sortableQnaList, index, { ...prevQna, data: { ...prevQna.data, question } }));
    };
    
    const onChangeQnaAnswer = (index: number, answer: string) => {
        const prevQna = sortableQnaList[index];
        update(withArray(sortableQnaList, index, { ...prevQna, data: { ...prevQna.data, answer } }));
    };

    const syncFromProps = useCallback(() => {
        setSortableQnaList(getUpdatedSortableList(sortableQnaList, qnaList));
    }, [qnaList, sortableQnaList]);

    useSyncForm(syncFromProps);

    return (
        <SortableWrapper items={sortableQnaList}
                         className="flex flex-col gap-1"
                         onDragSortEnd={update}
        >{sortableQnaList.map((qna, index) => (
                <SortableItemWrapper key={qna.id}
                                     item={qna}
                                     className="w-full"
                >{dragHandle => (
                    <div key={qna.id} className="w-full flex gap-4 items-start">
                        <div className="w-full flex gap-2.5 items-start">
                            <div className="label-input-grid-label">
                                {dragHandle}
                            </div>
                            <div className="w-full flex items-start gap-1">
                                <Textarea required
                                          aria-label={`${intl.formatMessage({ id: "b0038" })} ${index + 1}`}
                                          value={qna.data.question}
                                          placeholder={intl.formatMessage({ id: "b0038" })}
                                          onChange={question => onChangeQnaQuestion(index, question)}
                                />
                                <Textarea required
                                          aria-label={`${intl.formatMessage({ id: "b0039" })} ${index + 1}`}
                                          value={qna.data.answer}
                                          placeholder={intl.formatMessage({ id: "b0039" })}
                                          onChange={answer => onChangeQnaAnswer(index, answer)}
                                />
                            </div>
                        </div>
                        <button type="button"
                                className="mt-2.5 text-red-500 shrink-0"
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={() => update(toSpliced(sortableQnaList, index, 1))}
                        >
                            <DeleteIcon className="size-4" />
                        </button>
                    </div>
                )}</SortableItemWrapper>
        ))}</SortableWrapper>
    );
};

export default FAQInputGroup;
